import { Controller } from "@hotwired/stimulus"

const preferenceKey = "noble-capybara.sms.notifications"
const notifiedKey = "noble-capybara.sms.notified-ids"
const notificationLock = "noble-capybara.sms.notifications"

// Metadata polling is independent of history refresh/read acknowledgements.
// It keeps background tabs useful without touching drafts or scroll positions.
export class SmsActivityController extends Controller {
    static targets = ["checkbox", "notice", "teamState", "resolveAction", "attentionAction", "unreadSummary"]
    static values = { url: String, unread: Number, initialIds: Array, startedAt: String }

    connect() {
        this.stopped = false
        this.failures = 0
        this.seen = new Set(this.initialIdsValue)
        this.notifications = new Set()
        this.startedAt = Date.parse(this.startedAtValue)
        this.baseTitle = document.title.replace(/^\(\d+\)\s*/, "")
        this.updateTitle(this.unreadValue)
        this.syncPreference()
        this.storageChanged = event => {
            if (event.key === preferenceKey || event.key === null) {
                if (!this.enabled) this.suppressNextNotifications = true
                this.syncPreference()
                this.refresh()
            }
        }
        this.visibilityChanged = () => { if (!document.hidden) this.refresh() }
        this.beforeCache = () => this.disconnect()
        window.addEventListener("storage", this.storageChanged)
        document.addEventListener("visibilitychange", this.visibilityChanged)
        document.addEventListener("turbo:before-cache", this.beforeCache)
        this.schedule()
    }

    disconnect() {
        this.stopped = true
        clearTimeout(this.timer)
        this.abortController?.abort()
        window.removeEventListener("storage", this.storageChanged)
        document.removeEventListener("visibilitychange", this.visibilityChanged)
        document.removeEventListener("turbo:before-cache", this.beforeCache)
        this.closeNotifications()
    }

    storageGet(key) { try { return localStorage.getItem(key) } catch { return null } }
    storageSet(key, value) { try { localStorage.setItem(key, value); return true } catch { return false } }
    supported() { return window.isSecureContext && typeof Notification !== "undefined" }
    permitted() { return this.supported() && Notification.permission === "granted" }

    syncPreference() {
        this.enabled = this.permitted() && this.storageGet(preferenceKey) === "true"
        if (this.hasCheckboxTarget) {
            this.checkboxTarget.checked = this.enabled
            this.checkboxTarget.disabled = !this.supported() || Notification.permission === "denied" || this.stopped
        }
        if (!this.enabled) this.closeNotifications()
        this.say(!this.supported() ? "Desktop notifications are unavailable in this browser (HTTPS required)."
            : Notification.permission === "denied" ? "Notifications are blocked. Allow them in this site's browser settings to enable this option."
            : "")
    }

    // Only a checkbox gesture may request permission. Never prompt on load.
    async toggleNotifications() {
        const checked = this.checkboxTarget.checked
        this.checkboxTarget.disabled = true
        try {
            if (checked && this.supported() && Notification.permission === "default") {
                await Notification.requestPermission()
            }
            if (this.stopped) return
            const enabled = checked && this.permitted()
            // A fresh baseline prevents messages received during the permission
            // prompt from being presented as a new arrival after opting in.
            this.suppressNextNotifications = enabled
            const saved = this.storageSet(preferenceKey, enabled ? "true" : "false")
            this.syncPreference()
            if (!saved) this.say("Browser storage is unavailable; the notification setting could not be saved.")
            else if (enabled) await this.refresh()
        } catch {
            this.syncPreference()
            this.say("Unable to enable notifications. Check this site's browser notification settings.")
        }
    }

    updateTitle(count) {
        document.title = `${count > 0 ? `(${count}) ` : ""}${this.baseTitle}`
        if (this.hasUnreadSummaryTarget) this.unreadSummaryTarget.textContent = count === 0
            ? "No unread messages" : `${count} unread message${count === 1 ? "" : "s"}`
    }

    schedule() {
        clearTimeout(this.timer)
        if (!this.stopped) this.timer = setTimeout(() => this.refresh(), Math.min(60000, 10000 * 2 ** this.failures))
    }

    async refresh() {
        if (this.stopped || this.busy) return
        this.busy = true
        clearTimeout(this.timer)
        this.abortController = new AbortController()
        const timeout = setTimeout(() => this.abortController.abort(), 15000)
        try {
            const response = await fetch(this.urlValue, {
                credentials: "same-origin", cache: "no-store", signal: this.abortController.signal,
                headers: { Accept: "application/json" }
            })
            if (response.redirected || response.status === 401 || response.status === 403) {
                this.disconnect()
                this.updateTitle(0)
                if (this.hasCheckboxTarget) this.checkboxTarget.disabled = true
                this.say("SMS access expired. Reload and sign in again.")
                return
            }
            if (!response.ok) throw new Error("Activity unavailable")
            const snapshot = await response.json()
            if (this.stopped) return
            if (!Number.isSafeInteger(snapshot.unreadCount) || snapshot.unreadCount < 0 || !Array.isArray(snapshot.messages)) {
                throw new Error("Invalid activity response")
            }
            this.updateTitle(snapshot.unreadCount)
            if (typeof snapshot.needsAttention === "boolean") {
                if (this.hasTeamStateTarget) this.teamStateTarget.hidden = !snapshot.needsAttention
                if (this.hasResolveActionTarget) this.resolveActionTarget.hidden = !snapshot.needsAttention
                if (this.hasAttentionActionTarget) this.attentionActionTarget.hidden = snapshot.needsAttention
            }
            this.syncPreference()
            const suppress = this.suppressNextNotifications
            this.suppressNextNotifications = false
            // The server returns newest first; notify in chronological ID order.
            for (const message of [...snapshot.messages].reverse()) {
                if (typeof message.id !== "string" || !/^\d+$/.test(message.id)) continue
                if (this.seen.has(message.id)) continue
                this.seen.add(message.id)
                if (!suppress && this.enabled && Date.parse(message.createdAt) > this.startedAt) await this.notify(message)
            }
            // Keep only IDs (no contact names/numbers/bodies) in memory/storage.
            this.seen = new Set([...this.seen].slice(-1000))
            this.failures = 0
        } catch {
            if (!this.stopped) {
                this.failures++
                this.say("Live unread counts and notifications are temporarily unavailable; retrying.")
            }
        } finally {
            clearTimeout(timeout)
            this.busy = false
            this.schedule()
        }
    }

    async notify(message) {
        if (typeof message.contact !== "string" || typeof message.preview !== "string" || typeof message.url !== "string") return
        const url = new URL(message.url, window.location.origin)
        if (url.origin !== window.location.origin || !/^\/admin\/sms\/conversation\/\d+$/.test(url.pathname)) return
        const show = () => {
            if (this.stopped || !this.enabled || !this.permitted() || this.storageGet(preferenceKey) !== "true") return
            let ids
            try { ids = JSON.parse(this.storageGet(notifiedKey) || "[]") } catch { ids = [] }
            if (!Array.isArray(ids)) ids = []
            ids = ids.filter(id => typeof id === "string")
            if (ids.includes(message.id)) return
            // Unsupported implementations (notably mobile without a service
            // worker) may expose the API but reject the constructor.
            try {
                const notification = new Notification(message.contact, {
                    body: message.preview, tag: `noble-capybara-sms-${message.id}`
                })
                this.storageSet(notifiedKey, JSON.stringify([...ids, message.id].slice(-500)))
                this.notifications.add(notification)
                notification.onclose = () => this.notifications.delete(notification)
                notification.onclick = () => {
                    // Never navigate away from an unsent reply in this tab.
                    window.open(url.href, "_blank", "noopener")
                    notification.close()
                }
            } catch {
                this.say("This browser cannot display desktop notifications. Try a supported desktop browser.")
            }
        }
        // Coordinate tabs where Web Locks is available. The shared ID ledger
        // and notification tag provide best-effort deduplication otherwise.
        if (navigator.locks?.request) await navigator.locks.request(notificationLock, show)
        else show()
    }

    closeNotifications() {
        for (const notification of this.notifications || []) notification.close()
        this.notifications?.clear()
    }

    say(message) {
        if (this.hasNoticeTarget) {
            this.noticeTarget.textContent = message
            this.noticeTarget.hidden = !message
        }
    }
}
